import { Box, Button, ButtonProps, Container, Typography, styled } from '@mui/material'
import { BackButton } from '../components/BackButton'
import React, { ChangeEvent, ElementType, useContext, useState } from 'react'
import { HotelContext } from '../contexts/HotelContext'
import { readAsUrlAsync } from '../utils/fileUtil'
import { scanIdentityCard } from '../apis/checkinApis'
import { IdentityInfo } from '../types/checkinTypes'
import { OverlayLoader } from '../components/Loader'
import { IconCCCD } from '../icons/IconCCCD'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router'
import { CardImage } from '../components/CardImage'
import { language } from '../common/constants'
import { environment } from '../environments/environment'
import { getNextPage } from '../utils/checkinUtils'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

const StyledContainer = styled(Container)`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const IdentityCardScanner = () => {
  const { hotel, reservation, setIdentity, pauseSession, resumeSession } = useContext(HotelContext)
  const [identityResult, setIdentityResult] = useState<IdentityInfo>()
  const [imageUrl, setImageUrl] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (!file) {
      return
    }

    try {
      setIsLoading(true)
      const image = await readAsUrlAsync(file)
      setImageUrl(image)
      const base64Image = image.split(',')[1]
      // let result = await validateIdentityCard(hotel.hotelId, reservation.id, {
      //   base64Image,
      // })
      // if (!result?.idNumber) {
      //   throw new Error('Invalid image')
      // }

      const result = await scanIdentityCard(hotel.hotelId, reservation.id, {
        base64Image,
      })

      setIdentityResult(result)
      setIdentity(result.idNumber)
    } catch (err) {
      enqueueSnackbar(language.SCAN_ERROR, {
        variant: 'error',
      })
      window.newrelic?.noticeError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async () => {
    const { url } = getNextPage(reservation)
    navigate(url)
  }

  const buttonProps: Partial<ButtonProps<ElementType<HTMLLabelElement>>> = identityResult
    ? {
        sx: {
          textTransform: 'none',
          fontWeight: 'normal',
          color: (theme) => theme.palette.text.secondary,
        },
        color: 'inherit',
        variant: 'text',
        size: 'small',
      }
    : {
        variant: 'contained',
        size: 'large',
      }

  const inputProps: React.ComponentProps<typeof VisuallyHiddenInput> = environment.kiosk
    ? { capture: 'environment', accept: 'image/*' }
    : {
        accept: 'image/*',
      }

  return (
    <StyledContainer maxWidth="sm">
      <BackButton to={`/hotel/${hotel.hotelId}/checkin`} />
      {isLoading && <OverlayLoader />}
      <Box>
        <Typography
          color={(theme) => theme.palette.primary.main}
          mt={1}
          textAlign="center"
          variant="h4"
          gutterBottom
          fontWeight="600">
          Chụp ảnh mặt trước CCCD
        </Typography>
      </Box>
      {!imageUrl && (
        <Box padding={'2rem'} mt={2} textAlign="center">
          <IconCCCD width="100%" />
        </Box>
      )}
      {imageUrl && (
        <Box display="flex" flexDirection="column">
          <CardImage
            sx={{ width: '100%', height: '100%', minHeight: '250px', paddingTop: 0 }}
            contain
            image={imageUrl}
          />
          {identityResult && (
            <Box flex={1} mt={2} display="inline-block" textAlign="left">
              <Typography>
                <span>Số: </span>
                <b>{identityResult.idNumber}</b>
              </Typography>
              <Typography>
                <span>Họ và tên: </span>
                <b>{identityResult.fullname}</b>
              </Typography>
              <Typography>
                <span>Ngày sinh: </span>
                <b>{identityResult.dateOfBirth}</b>
              </Typography>
              <Typography>
                <span>Địa chỉ: </span>
                <b>{identityResult.placeOfResidence}</b>
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box>
        {identityResult && (
          <Button size="large" disabled={isLoading} fullWidth variant="contained" onClick={onSubmit}>
            Xác nhận
          </Button>
        )}
        <Button size="large" component="label" {...buttonProps} disabled={isLoading} fullWidth onClick={pauseSession}>
          {identityResult ? 'Chụp lại ảnh khác' : 'Nhấn để chụp ảnh CCCD'}
          <VisuallyHiddenInput type="file" {...inputProps} onFocus={resumeSession} onChange={handleChange} />
        </Button>
      </Box>
    </StyledContainer>
  )
}
